import { computed, inject, provide } from 'vue'
import { useRoute } from 'vue-router'

import { useAuthStore } from '@/features/auth/useAuthStore.js'
import { decidePermission } from '@/features/permissions/permissionEngine.js'
import { getActiveClubId, useActiveClub } from '@/features/router/useActiveClub.js'

export const permissionContextInjectionKey = Symbol('permission-context')

export function providePermissionContext(context) {
  provide(permissionContextInjectionKey, context)
}

export function usePermission(defaultContext = {}) {
  const injectedContext = inject(permissionContextInjectionKey, {})
  const authStore = useAuthStore()
  const route = useRoute()
  const { activeClub } = useActiveClub()

  function buildContext(context = {}) {
    return {
      myDetails: authStore.myDetails,
      activeTenantId: getActiveClubId(),
      activeTenant: typeof activeClub === 'object' ? activeClub : null,
      surface: route.path?.startsWith('/super-admin') ? 'super-admin' : 'admin',
      route: route.fullPath || route.path,
      ...injectedContext,
      ...defaultContext,
      ...context,
    }
  }

  function decision(action, context = {}) {
    return decidePermission(action, buildContext(context))
  }

  function can(action, context = {}) {
    return decision(action, context).allowed
  }

  function useDecision(action, context = {}) {
    return computed(() => decision(action, context))
  }

  return {
    can,
    decision,
    useDecision,
  }
}
